/* drCarried.js -- "IS THIS DEFERRED-REVENUE ACCOUNT CARRIED BY A REVENUE STREAM?" -- ONE PREDICATE (W-138, 2026-10-05).
 *
 * A balance-sheet line typed deferred revenue (role 'deferred_revenue', Step 4a) gets its forecast balance ONLY from the
 * revenue stream that links it: an AE Quota / Company Quota module (its Step-7 Balance Sheet line, rmm.bs_line_key), a
 * deferred implementation line linked to it (Deferred Revenue page), or -- until such a line exists -- a Step-7
 * RESERVATION for implementation revenue (companies.dr_line_reservations; P20 then waits for the line). With none, it
 * books 0 from the first forecast month, silently: cash moves by the whole balance, the balance check stays at 0.
 *
 * TWO READERS, ONE RULE:
 *   - Step 7's save refuses an uncarried account (AdminPage handleStep7Save, 2026-09-24);
 *   - the pre-flight's P8 blocks go-live on one (W-138) -- the case Step 7 cannot see: a Step-4 re-import AFTER Step 7
 *     that adds (or retypes) a deferred-revenue account, because a re-import does not reopen Step 7.
 * A second definition of "carried" is the two-sums defect (CLAUDE.md section 6).
 */
import { implDrLinkedKey } from './implKeys';
import { MODULE_TYPE_TO_BS_CONFIG } from '../engines/buildBSLines';

/** The module types whose deferred-revenue line is wired by rmm.bs_line_key -- the builder's own table. */
export const DR_STREAM_MODULE_TYPES = Object.keys(MODULE_TYPE_TO_BS_CONFIG);

/** The real deferred-revenue accounts the implementation lines link: enabled, deferred, a REAL account (never the line's
 *  own synthetic) -- the builder's predicate. [{ id, name, key }]. */
export function implDeferredLinks(implementationLines) {
  return (implementationLines || [])
    .filter(l => l && l.enabled !== false && l.recognition_mode === 'deferred')
    .map(l => ({ id: l.id, name: l.name || ('implementation line ' + l.id), key: implDrLinkedKey(l.id, l.bs_line_key) }))
    .filter(x => x.key);
}

/** The deferred-revenue accounts NO stream carries.
 *    accounts      [{ id, label }] -- deferredRevenueOptionsFromMeta (role 'deferred_revenue' leaves)
 *    streams       rows with { module_type, bs_line_key } -- revenue_module_mappings, or Step 7's unsaved rows
 *    implLinks     implDeferredLinks(...)
 *    reservations  an iterable of reserved account keys (Step 7's Set, or Object.keys(dr_line_reservations)) */
export function uncarriedDeferredRevenueAccounts({ accounts, streams, implLinks, reservations }) {
  const carried = new Set();
  for (const s of (streams || [])) if (s && DR_STREAM_MODULE_TYPES.includes(s.module_type) && s.bs_line_key) carried.add(s.bs_line_key);
  for (const x of (implLinks || [])) if (x && x.key) carried.add(x.key);
  for (const k of (reservations || [])) if (k) carried.add(k);
  return (accounts || []).filter(o => o && !carried.has(o.id));
}
